import * as Msdyn365 from '@msdyn365-commerce/core';
import { OPEN_STATUS } from '../../configuration/order-history';
import { ErrorCode } from '../../modules/order-details';
import { ensureAuthenticated } from '../../utilities/is-authenticated';
import { escapeODataNumberLiteral, escapeODataStringLiteral } from '../../utilities/odata';
import { trackedAction } from '../../utilities/track-execution-time';

/**
 * GetOrderHistory Input Action
 */
export class GetOrderHistoryInput implements Msdyn365.IActionInput {
    public userToken: string;
    public status: string;
    public numMonths: Number;
    public timestamp: string;

    constructor(ctx: Msdyn365.ICreateActionContext, status: string, numMonths: Number) {
        this.userToken = ctx.requestContext.user.token;
        this.status = status;
        this.numMonths = numMonths;
        this.timestamp = new Date().getTime().toString();
    }

    public getCacheKey = () => `GetOrderHistory-${this.userToken}-${this.timestamp}`;
    public getCacheObjectType = () => 'GetOrderHistory';
    public dataCacheType = (): Msdyn365.CacheType => 'application';
}

export interface IOrderHistoryLine {
    OrderNumber: string;
    LineNumber: string;
    ProductName: string;
    Status: string;
    RequestedQty: string;
    RequestedDate?: string;
    RequestedUnit: string;
    Currency: string;
    NetValue: string;
    ScheduleLines: IOrderHistorySchedulLine[];
    ExtensionProperties: any[];
    ProductNumber: string;
    CustomerProductNumber?: string;
    BlNumber?: string;
    ContainerType?: string;
    Container?: string;
    ShipFlightNumber?: string;
    CarrierName?: string;
    ActualArrivalDate?: string;
    EstimatedArrivalDate?: string;
    StateLogistics: string;
    StateInvoice: string;
    DeliveryNumber?: string;
}
export interface IOrderHistorySchedulLine {
    OrderNumber: string;
    LineNumber: string;
    ScheduleLineNumber: string;
    ConfirmedQty: string;
    ConfirmedUnit: string;
    ConfirmedDate?: string;
    ExtensionProperties: any[];
}

export interface IGetOrderHistoryData {
    '@odata.context'?: string;
    ExtensionProperties: string[];
    Orders: IOrderHistoryTableData[];
    RecordCreatedDate: string;
    ReturnCodes: number[];
    error?: ErrorCode;
}

export interface IOrderHistoryTableData {
    OrderNumber: string;
    PoNumber: string;
    Status: string;
    ConfirmedDate: string;
    SoldToId: string;
    SoldToName: string;
    ShipToId: string;
    ShipToName: string;
    RecordCreatedDate: string;
    OrderLines: IOrderHistoryLine[];
    ExtensionProperties: any[];
    highlight: boolean;
    highlightOrderLines: boolean;
    StateLogistics: string;
    StateInvoice: string;
    ReorderAllowed?: boolean;
}

const createInput = (args: Msdyn365.ICreateActionContext): Msdyn365.IActionInput => {
    return new GetOrderHistoryInput(args, '', 0);
};

export async function action(input: GetOrderHistoryInput, ctx: Msdyn365.IActionContext): Promise<IGetOrderHistoryData> {
    ensureAuthenticated(ctx);
    const response = await Msdyn365.sendRequest<IGetOrderHistoryData>(
        `${ctx.requestContext.apiSettings.baseUrl}Commerce/GetHistory(language='${escapeODataStringLiteral(
            ctx.requestContext.locale
        )}',status='${escapeODataStringLiteral(input.status || OPEN_STATUS)}',numMonths=${escapeODataNumberLiteral(input.numMonths)})`,
        'get',
        {},
        {
            headers: {
                OUN: ctx.requestContext.apiSettings.oun,
                Authorization: `id_token ${ctx.requestContext.user.token}`
            },
            timeout: 30000
        }
    );
    if (response.status === 200 && response.data) {
        return { ...response.data };
    }
    return { ...({} as IGetOrderHistoryData), error: { errorCode: response.status, errorText: response.statusText } };
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<IGetOrderHistoryData>>((input, ctx) => trackedAction(action, input, ctx, 'GetHistory')),
    id: 'GetOrderHistory',
    input: createInput
});
